import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { IdTableVariant } from "../../../../@types/enumsGlobal";
import useOperativeReserve from "../../../../components/lotSelector/useOperativeReserve";
import { IDocumentInfoDTO, IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common";
import { IInvoiceCashOrderGetDTO, IInvoiceCashOrderEditDTO } from "../../../../libs/coreapi-dto/documents/invoiceCashOrder";
import { v4 as uuidv4, NIL as uuidNull } from "uuid";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { roundMoneyFormat } from "../../../../system/functions/round";

export const useInvoiceCashOrderState = (idGlobal: string, defaultEntity: IInvoiceCashOrderGetDTO | null | undefined, callback: (entity: IInvoiceCashOrderEditDTO) => void) => {
    const userContext = useUserContext();
    
    const [contractorOwner, setContractorOwner] = useState<IEntitySimpleDTO | null>(null);
    const [contractorFrom, setContractorFrom] = useState<IEntitySimpleDTO | null>(defaultEntity?.contractorFrom ?? null);

    //переменные-состояния для ставки НДС
    const [supplierTaxType, setSupplierTaxType] = useState<IEntitySimpleDTO | null>(defaultEntity?.taxType ?? null);
    const [taxRate, setTaxRate] = useState<number | null>(null);

    const [isIncludeVATActive, setIsIncludeVATActive] = useState<boolean>(
        (!defaultEntity || defaultEntity?.documentState === 'save' || defaultEntity?.documentState === 'draft') ? true : false);

    //переменные-состояния для базовых документов
    const [baseDocument, setBaseDocument] = useState<string>(defaultEntity?.baseDocumentMnemocode ?? '');

    const [entity, setEntity] = useState<IInvoiceCashOrderEditDTO>({
        documentDate: DateTime.now()
    } as IInvoiceCashOrderEditDTO);


    useEffect(() => {
        callback({
            ...entity,
        });
    }, [defaultEntity, entity]);

    const operativeReserve = useOperativeReserve();
    // console.log('entity in State', entity);

    const updateEntity = (updater: (prev: IInvoiceCashOrderEditDTO) => Partial<IInvoiceCashOrderEditDTO>) => {
        setEntity(prev => ({ ...prev, ...updater(prev) }));
    };

    return {
        document: {
            value: entity,
        },
        entitySet: {
            setValue: (value: IInvoiceCashOrderGetDTO | null) => {
                setEntity({
                    idGlobal: value?.idGlobal as string,
                    // documentDate: value?.documentDate.toF3DateTimeServerSide(userContext),
                    documentDate: value?.documentDate 
                        ? value.documentDate.toF3DateTimeServerSide(userContext) 
                        : DateTime.now(),
                    baseDocumentInfo : value?.baseDocumentInfo,
                    supplement : value?.supplement,
                    idTaxTypeGlobal : value?.taxType?.idGlobal ? value?.taxType.idGlobal : '',
                    baseText : value?.baseText,
                    idUserGlobal : value?.userAuthor?.idGlobal,
                    idContractsGlobal : value?.contracts?.idGlobal,
                    includeVat : value?.includeVat,
                    idContractorOwnerGlobal : value?.contractorOwner?.idGlobal,
                    idContractorFromGlobal : value?.contractorFrom?.idGlobal,
                    supplierSum : roundMoneyFormat(value?.supplierCostInfo?.sum),
                    supplierVatSum : roundMoneyFormat(value?.supplierCostInfo?.vatSum),
                    supplierSumIncVat : roundMoneyFormat(value?.supplierCostInfo?.sumIncVat),
                    retailSum : roundMoneyFormat(value?.retailCostInfo?.sum),
                    retailVatSum : roundMoneyFormat(value?.retailCostInfo?.vatSum),
                    retailSumIncVat : roundMoneyFormat(value?.retailCostInfo?.sumIncVat)
                } as IInvoiceCashOrderEditDTO)
            }
        },
        idGlobal: entity.idGlobal,
        documentDate: {
            value: entity.documentDate,
            setValue: (value: DateTime) => updateEntity(() => ({ documentDate: value })),
        },
        idContractorFromGlobal : {
            value: entity.idContractorFromGlobal,
            setValue: (value: string) => updateEntity(() => ({ idContractorFromGlobal: value })),
        },
        idContractorOwnerGlobal : {
            value: entity.idContractorOwnerGlobal,
            setValue: (value: string) => updateEntity(() => ({ idContractorOwnerGlobal: value })),
        },
        idContractsGlobal : {
            value: entity.idContractsGlobal,
            ssetValue: (value: string) => updateEntity(() => ({ idContractsGlobal: value })),
        },
        idTaxTypeGlobal : {
            value: entity.idTaxTypeGlobal,
            setValue: (value: string) => updateEntity(() => ({ idTaxTypeGlobal: value })),
        },
        supplierSum : {
            value: entity.supplierSum,
            setValue: (value: number) => updateEntity(() => ({ supplierSum: roundMoneyFormat(value) })),
        },
        supplierVatSum : {
            value: entity.supplierVatSum,
            setValue: (value: number) => updateEntity(() => ({ supplierVatSum: roundMoneyFormat(value) })),
        },
        supplierSumIncVat : {
            value: entity.supplierSumIncVat,
            setValue: (value: number) => updateEntity(() => ({ supplierSumIncVat: roundMoneyFormat(value) })),
        },
        retailSum : {
            value: entity.retailSum,
            setValue: (value: number) => updateEntity(() => ({ retailSum: roundMoneyFormat(value) })),
        },
        retailVatSum : {
            value: entity.retailVatSum,
            setValue: (value: number) => updateEntity(() => ({ retailVatSum: roundMoneyFormat(value) })),
        },
        retailSumIncVat : {
            value: entity.retailSumIncVat,
            setValue: (value: number) => updateEntity(() => ({ retailSumIncVat: roundMoneyFormat(value) })),
        },
        baseDocumentInfo : {
            value: entity.baseDocumentInfo,
            setValue: (value: IDocumentInfoDTO) => updateEntity(() => ({ baseDocumentInfo: value })),
        },
        baseText : {
            value: entity.baseText,
            setValue: (value: string) => updateEntity(() => ({ baseText: value })),
        }, 
        supplement : {
            value: entity.supplement,
            setValue: (value: string) => updateEntity(() => ({ supplement: value })),
        },
        contractorFromClear: {
            setValue: (idContractorFromGlobal: string, retailSum: number, idTaxTypeGlobal: string, contractorFrom: IEntitySimpleDTO | null) => {
                updateEntity(() => ({ 
                    idContractorFromGlobal: idContractorFromGlobal, 
                    retailSum: roundMoneyFormat(retailSum), 
                    idTaxTypeGlobal: idTaxTypeGlobal 
                }));
                setContractorFrom(contractorFrom);
            },
        },
        contractorFrom: {
            value: contractorFrom,
            setValue: (value: IEntitySimpleDTO) => setContractorFrom(value),
        },
        contractorDefaultSet: {
            setValue: (idContractorFromGlobal: string, contractorFrom: IEntitySimpleDTO | null) => {
                updateEntity(() => ({ idContractorFromGlobal: idContractorFromGlobal }));
                // setContractorFrom(contractorFrom); // должен заполняться только Основной контрагент
            }
        },
        taxRateSet: {
            setValue: (idTaxTypeGlobal: string, retailVatSum: number) => {
                updateEntity(() => ({ 
                    idTaxTypeGlobal: idTaxTypeGlobal, 
                    retailVatSum: roundMoneyFormat(retailVatSum) 
                }));
            }
        },
        contractorOwner: {
            value: contractorOwner,
            setValue: (value: IEntitySimpleDTO) => setContractorOwner(value),
        },
        contractorOwnerSet: {
                setValue: (idContractorOwnerGlobal: string, contractorOwner: IEntitySimpleDTO | null) => {
                updateEntity(() => ({ idContractorOwnerGlobal: idContractorOwnerGlobal }));
                setContractorOwner(contractorOwner);
            }
        },
        contractorFromSet: {
                setValue: (isContractorFromNotChanged: boolean, idContractorFromGlobal: string, contractorFrom: IEntitySimpleDTO | null,
                    retailSumIncVat: number, idTaxTypeGlobal: string, retailVatSum: number, baseDocumentInfo: IDocumentInfoDTO
                ) => {
                    updateEntity(() => ({ 
                        idContractorFromGlobal: idContractorFromGlobal, 
                        retailSumIncVat: roundMoneyFormat(retailSumIncVat),
                        idTaxTypeGlobal: idTaxTypeGlobal, 
                        retailVatSum: roundMoneyFormat(retailVatSum), 
                        baseDocumentInfo: baseDocumentInfo
                    }));
                setContractorFrom(contractorFrom);
                if (!isContractorFromNotChanged) {
                    setSupplierTaxType(null); 
                    setTaxRate(0);
                    setBaseDocument('');
                }
        }},
        supplierTaxType: {
            value: supplierTaxType,
            setValue: (value: IEntitySimpleDTO) => setSupplierTaxType(value),
        },
        taxRate: {
            value: taxRate,
            setValue: (value: number | null) => setTaxRate(value),
        },
        isIncludeVATActive: {
            value: isIncludeVATActive,
            setValue: (value: boolean) => setIsIncludeVATActive(value),
        },
        baseDocument: {
            value: baseDocument,
            setValue: (value: string) => setBaseDocument(value),
        },
        sumPaySet: {
                setValue: (retailSumIncVat: number, retailVatSum: number) => {
                updateEntity(() => ({ 
                    retailSumIncVat: roundMoneyFormat(retailSumIncVat), 
                    retailVatSum: roundMoneyFormat(retailVatSum) 
                }));
        }},
        taxTypeSet: {
                setValue: (supplierTaxType: IEntitySimpleDTO | null, idTaxTypeGlobal: string) => {
                updateEntity(() => ({ idTaxTypeGlobal: idTaxTypeGlobal }));
                setSupplierTaxType(supplierTaxType);
                
        }},
        taxTypeClear: {
                setValue: (vatSumOrigin: number, idTaxTypeGlobal: string) => {
                    setSupplierTaxType(null); 
                    setTaxRate(null);
                    updateEntity(() => ({ 
                        idTaxTypeGlobal: idTaxTypeGlobal, 
                        retailVatSum: roundMoneyFormat(vatSumOrigin) 
                    }));
                }
        },
        vatSet: {
                    setValue: (retailVatSum: number, idTaxTypeGlobal: string) => {
                    
                        updateEntity(() => ({ 
                            idTaxTypeGlobal: idTaxTypeGlobal, 
                            retailVatSum: roundMoneyFormat(retailVatSum) 
                        }));
                    setSupplierTaxType(null); 
                    setTaxRate(0);
                }
        },
        baseDocumentSet: {
            setValue: (document: IInvoiceCashOrderEditDTO, baseDocumentInfo: { idTable: number; idGlobal: string; displayName: string }, documentCostInfo: IDocumentItemCostInfoDTO, contractorTo: IEntitySimpleDTO) => {
            setEntity({                
                    idGlobal: document?.idGlobal as string,
                    documentDate: document?.documentDate.toF3DateTimeServerSide(userContext),
                    baseDocumentInfo : { idTable: baseDocumentInfo.idTable, idDocument: baseDocumentInfo.idGlobal },
                    supplement : document?.supplement,
                    idTaxTypeGlobal : uuidNull, 
                    baseText : `${baseDocumentInfo.displayName}`,
                    idUserGlobal : document?.idUserGlobal,
                    idContractsGlobal : document?.idContractsGlobal,
                    includeVat : document?.includeVat,
                    idContractorOwnerGlobal : document?.idContractorOwnerGlobal,
                    idContractorFromGlobal : contractorTo.idGlobal,
                    supplierSum : roundMoneyFormat(document?.supplierSum),
                    supplierVatSum : roundMoneyFormat(document?.supplierVatSum),
                    supplierSumIncVat : roundMoneyFormat(document?.supplierSumIncVat),
                    retailSum : roundMoneyFormat(document?.retailSum),
                    retailVatSum : roundMoneyFormat(documentCostInfo.retailCostInfo.vatSum),
                    retailSumIncVat : roundMoneyFormat(documentCostInfo.retailCostInfo.sumIncVat),
                });    
            setSupplierTaxType(null);
            setTaxRate(null);
            setIsIncludeVATActive(false);
            setContractorFrom(contractorTo);  
            }
        },
        baseDocumentClear: {
            setValue: () => {
                setIsIncludeVATActive(true);
                updateEntity(() => ({ 
                    baseDocumentInfo: { idTable: 0, idDocument: uuidNull },
                    idTaxTypeGlobal: uuidNull, 
                    baseText: ''
                })); 
            }
        },
    };
};

export default useInvoiceCashOrderState;
